/* eslint-disable require-atomic-updates */
import getIn from 'lodash/get';
import isNumber from 'lodash/isNumber';
import { call, put, select } from 'redux-saga/effects';

import * as APSActions from '../../actions';
import * as APSConstants from '../../constants';
import * as D from '../../deps';
import * as APSSelectors from '../../selectors';
import * as APSServices from '../../services';

import * as Helpers from './helpers';

const spawn = D.taskManagerSpawnFactory(APSConstants.DOMAIN_NAME);

export function* addItemFeedback(action) {
  const sessionItem = (yield select(
    APSSelectors.getSessionItem,
    getIn(action, 'data.itemPandoraId'),
  )).toJS();
  sessionItem.elapsedTime = yield select(APSSelectors.getCurrentItemElapsedTime);
  sessionItem.deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
  sessionItem.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
  const isPositive = getIn(action, 'data.isPositive');
  const service = isPositive ? APSServices.actionThumbUp : APSServices.actionThumbDown;
  try {
    yield call(service, sessionItem);
  } catch (error) {
    yield call(Helpers.onActionEventCurrentFailure, {
      error,
      apsEndpoint: isPositive
        ? D.Constants.Api.ApiEndpoints.ACTION_THUMB_UP
        : D.Constants.Api.ApiEndpoints.ACTION_THUMB_DOWN,
      originalAction: action,
    });
  }
}

export function* playCurrentItem(action) {
  let item = action.data.item;
  if (!item) {
    const result = yield call(Helpers.playbackCurrent, action);
    item = result?.item;
  }

  const nextItem = yield select(APSSelectors.getNextItem);
  if (nextItem.get('trackToken') && item && item.trackToken === nextItem.get('trackToken')) {
    item = nextItem.toJS();
  }

  if (!item) {
    return;
  }

  const isStationPlayback = yield select(D.Utils.Listener.isStationPlayback);
  if (isStationPlayback) {
    item.originalSourceId = item.sourceId;
    item.sourceId = action.data.sourceId;
  }

  const prevItem = yield select(APSSelectors.getCurrentItem);
  const isNew =
    prevItem.get('index') !== item.index ||
    prevItem.get('sourceId') !== item.sourceId ||
    prevItem.get('pandoraId') !== item.pandoraId;
  yield call(Helpers.playItem, { item, isNew });
}

export function* playAdItem(action) {
  const item = action.data.item;

  if (!item) return;

  yield call(Helpers.playItem, { item });
}

export function* itemEnded(action) {
  const item = (yield select(APSSelectors.getCurrentItem)).toJS();
  if (
    !item?.isAdvertisement &&
    item.sourceId === action.data.sourceId &&
    item.pandoraId === action.data.itemPandoraId
  ) {
    item.reason = action.data.reason || APSConstants.APSTrackEndReasons.NORMAL;
    const currentItemElapsedTime = yield select(APSSelectors.getCurrentItemElapsedTime);
    item.elapsedTime = action.data.elapsedTime || currentItemElapsedTime;
    item.deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
    item.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    try {
      const result = yield call(APSServices.eventEnded, item);
      yield call(playCurrentItem, {
        data: { sourceId: item.sourceId, item: result.item },
      });
    } catch (error) {
      yield spawn(Helpers.onActionEventCurrentFailure, {
        error,
        apsEndpoint: D.Constants.Api.ApiEndpoints.EVENT_ENDED,
        originalAction: action,
      });
    }
  }
}

export function* itemStarted(action) {
  const item = (yield select(APSSelectors.getCurrentItem)).toJS();
  if (!item?.isAdvertisement && item.pandoraId === action.payload.token) {
    item.deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
    item.elapsedTime = yield select(APSSelectors.getCurrentItemElapsedTime);
    item.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    try {
      yield call(APSServices.eventStarted, item);
    } catch (error) {
      yield call(Helpers.onActionEventCurrentFailure, {
        error,
        apsEndpoint: D.Constants.Api.ApiEndpoints.EVENT_STARTED,
        originalAction: action,
      });
    }
  }
}

export function* itemUpdate(action) {
  const item = (yield select(APSSelectors.getCurrentItem)).toJS();
  if (item.pandoraId === action.payload.token && !item?.isAdvertisement) {
    const elapsedTime = action.payload[D.Constants.AudioPlayer.AudioPlayerKeys.CURRENT_TIME];

    if (item.duration - elapsedTime < 30) {
      const nextItem = yield select(APSSelectors.getNextItem);
      if (nextItem.isEmpty() || D.Utils.Time.getNow() - nextItem.get('timestamp') > 60000) {
        yield spawn(Helpers.peekSource, item.sourceId);
      }
    }

    const pandoraType = D.Utils.SourceType.getPandoraTypeFromPandoraId(item.sourceId);
    const isStationType = D.Utils.SourceType.isStationType(pandoraType);
    const defaultProgressFrequency = 60;
    const progressFrequency =
      !item.progressFrequency && isStationType ? defaultProgressFrequency : item.progressFrequency;

    if (progressFrequency) {
      const shouldSendProgress =
        elapsedTime > 1 && Math.floor(elapsedTime) % progressFrequency === 0;
      if (shouldSendProgress) {
        item.deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
        item.elapsedTime = elapsedTime;
        item.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
        try {
          yield call(APSServices.eventProgress, item);
        } catch (error) {
          yield call(Helpers.onActionEventCurrentFailure, {
            error,
            apsEndpoint: D.Constants.Api.ApiEndpoints.EVENT_PROGRESS,
            originalAction: action,
          });
        }
      }
    }
  }
}

export function* pauseSource(action) {
  const currentItem = (yield select(APSSelectors.getCurrentItem)).toJS();
  if (isNumber(currentItem.index)) {
    currentItem.deviceProperties = yield select(
      D.getInDomain('analytics.utils.getAPSEventData', {}),
    );
    currentItem.elapsedTime = yield select(APSSelectors.getCurrentItemElapsedTime);
    currentItem.onUnload = getIn(action, 'data.onUnload');
    currentItem.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    try {
      yield call(APSServices.actionPause, currentItem);
    } catch (error) {
      yield call(Helpers.onActionEventCurrentFailure, {
        error,
        apsEndpoint: D.Constants.Api.ApiEndpoints.ACTION_PAUSE,
        originalAction: action,
      });
    }
  }
}

export function* playSource(action) {
  if (yield select(D.PlaybackUtils.isAudioPlaying)) {
    yield call(pauseSource);
  }

  const result = yield call(Helpers.playbackSource, action.data);
  if (result) {
    yield call(Helpers.playItem, {
      item: result.item,
      repeat: getIn(result, 'source.repeat'),
      shuffle: getIn(result, 'source.shuffle'),
      isNew: true,
    });
  }
}

export function* refreshCurrentItem(action) {
  const currentItem = yield select(APSSelectors.getCurrentItem);
  yield call(playCurrentItem, {
    data: { sourceId: currentItem.get('sourceId'), item: null },
  });
}

export function* resetCurrentSource(action) {
  yield call(Helpers.pauseAudioIfPlaying);
  const result = yield call(Helpers.playbackResetCurrentSource);
  if (result) {
    yield call(Helpers.setPlaylistAndResetIndex, result);
    yield call(D.putAndWait, APSActions.setCurrentItem({ item: result.item }));
  }
}

export function* removeItemFeedback(action) {
  const sessionItem = (yield select(
    APSSelectors.getSessionItem,
    getIn(action, 'data.itemPandoraId'),
  )).toJS();
  sessionItem.elapsedTime = yield select(APSSelectors.getCurrentItemElapsedTime);
  sessionItem.isPositive = getIn(action, 'data.isPositive');
  sessionItem.deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
  sessionItem.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
  sessionItem.stationId = getIn(action, 'data.sourceId').split(':').pop();
  try {
    yield call(APSServices.actionRemoveThumb, sessionItem);
  } catch (error) {
    yield call(Helpers.onActionEventCurrentFailure, {
      error,
      apsEndpoint: D.Constants.Api.ApiEndpoints.ACTION_REMOVE_THUMB,
      originalAction: action,
    });
  }
}

export function* repeatSource(action) {
  const isPlaybackStillAPS = yield select(D.PlaybackUtils.isPlaylistModelTypeAPSItem);
  if (isPlaybackStillAPS) {
    action.data.prevMode = yield select(APSSelectors.getSourceRepeatMode);
    action.data.deviceProperties = yield select(
      D.getInDomain('analytics.utils.getAPSEventData', {}),
    );
    action.data.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    try {
      yield call(APSServices.actionRepeat, action.data);
    } catch (error) {
      yield call(Helpers.onActionEventCurrentFailure, {
        error,
        apsEndpoint: D.Constants.Api.ApiEndpoints.ACTION_REPEAT,
        originalAction: action,
      });
    }
  }
}

export function* replayItem(action) {
  const isPlaybackStillAPS = yield select(D.PlaybackUtils.isPlaylistModelTypeAPSItem);
  if (isPlaybackStillAPS) {
    action.data = (yield select(
      APSSelectors.getSessionItem,
      getIn(action, 'data.itemPandoraId'),
    )).toJS();
    action.data.elapsedTime = yield select(APSSelectors.getCurrentItemElapsedTime);
    action.data.deviceProperties = yield select(
      D.getInDomain('analytics.utils.getAPSEventData', {}),
    );
    action.data.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    try {
      const result = yield call(APSServices.actionReplay, action.data);
      yield call(playCurrentItem, {
        data: { sourceId: getIn(action, 'data.sourceId'), item: result.item },
      });
    } catch (error) {
      yield spawn(Helpers.onActionEventCurrentFailure, {
        error,
        apsEndpoint: D.Constants.Api.ApiEndpoints.ACTION_REPLAY,
        originalAction: action,
      });
    }
  }
}

export function* loadArtistThroughSources(action) {
  const { item } = action.response;

  const sourceType = D.Utils.SourceType.getSourceTypeFromPandoraId(item.sourceId);
  if (
    sourceType === D.Constants.SourceTypes.SourceTypes.ARTIST_TOP_SONGS ||
    sourceType === D.Constants.SourceTypes.SourceTypes.ARTIST_ALL_SONGS ||
    sourceType === D.Constants.SourceTypes.SourceTypes.COLLECTED_ALBUM_TRACKS
  ) {
    const pandoraId = `${D.Constants.SourceTypes.PandoraTypes.ARTIST}:${item.sourceId
      .split(':')
      .pop()}`;
    const missingIds = yield select(D.getInDomain('catalog.utils.getIdsOfMissingAnnotations'), [
      pandoraId,
    ]);
    if (missingIds.size > 0)
      yield put(D.getInDomain('catalog.actions.annotateObjectsSimple')(missingIds));
  }
}

export function* resumeSource(action) {
  let item = (yield select(APSSelectors.getCurrentItem)).toJS();
  const isInitialLoad = !(yield select(D.PlaybackUtils.hasPlaybackStartedInSession));
  const isCurrentItemSimStreamViolation =
    item.type === APSConstants.APSItemTypes.SimStreamViolation;
  let isNew = false;
  if (isInitialLoad || isCurrentItemSimStreamViolation) {
    action.data = { sourceId: item.sourceId, forceActive: true };
    const result = yield call(Helpers.playbackCurrent, action);
    result.item.originalSourceId = result.item.sourceId;
    result.item.sourceId = result.source?.pandoraId || action.data.sourceId;
    if (!result) {
      return;
    }

    if (
      getIn(result, 'item.index') !== item.index ||
      getIn(result, 'item.pandoraId') !== item.pandoraId
    ) {
      isNew = true;
    }
    item = result.item;
  }

  yield call(Helpers.playItem, {
    item,
    time: isInitialLoad ? null : yield select(APSSelectors.getCurrentItemElapsedTime),
    isNew,
  });
}

export function* shuffleSource(action) {
  const isPlaybackStillAPS = yield select(D.PlaybackUtils.isPlaylistModelTypeAPSItem);
  if (isPlaybackStillAPS) {
    action.data.deviceProperties = yield select(
      D.getInDomain('analytics.utils.getAPSEventData', {}),
    );
    action.data.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    try {
      yield call(APSServices.actionShuffle, action.data);
    } catch (error) {
      yield call(Helpers.onActionEventCurrentFailure, {
        error,
        apsEndpoint: D.Constants.Api.ApiEndpoints.ACTION_SHUFFLE,
        originalAction: action,
      });
    }
  }
}

export function* getNextPlayablePlaylistIndex(sourceId, pandoraId, isSkipBack) {
  const trackIds = yield call(Helpers.getPlaylistTrackIds, sourceId);
  if (!trackIds) {
    return null;
  }
  const currentIndex = trackIds?.indexOf?.(pandoraId) ?? -1;
  const trackListSize = trackIds.size;
  const step = isSkipBack ? -1 : 1;

  const autoplayEnabled = yield select(D.Utils.Listener.isAutoplayEnabled);
  const nextIndex =
    !isSkipBack && !autoplayEnabled && currentIndex === trackListSize - 1 ? 0 : currentIndex + step;
  if (nextIndex < 0 || nextIndex >= trackListSize) {
    return null;
  }

  const playableIndex = yield call(Helpers.findNextPlayableTrackIndex, trackIds, nextIndex, step);
  if (playableIndex !== null) {
    return playableIndex === nextIndex ? null : playableIndex;
  }

  if (isSkipBack) {
    return currentIndex;
  }

  if (!autoplayEnabled) {
    const firstPlayableIndex = yield call(Helpers.findNextPlayableTrackIndex, trackIds, 0, 1);
    if (firstPlayableIndex !== null) {
      return firstPlayableIndex;
    }
  }

  return trackListSize;
}

export function* skipItem(action) {
  const isPlaybackStillAPS = yield select(D.PlaybackUtils.isPlaylistModelTypeAPSItem);
  if (isPlaybackStillAPS) {
    const item = (yield select(APSSelectors.getCurrentItem)).toJS();
    if (item.type === APSConstants.APSItemTypes.SimStreamViolation) {
      yield call(Helpers.playItem, { item });
      return;
    }

    action.data.deviceProperties = yield select(
      D.getInDomain('analytics.utils.getAPSEventData', {}),
    );
    action.data.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    action.data.elapsedTime = yield select(APSSelectors.getCurrentItemElapsedTime);
    try {
      let result;
      const modePandoraId = getIn(action, 'data.modePandoraId');
      const relativeIndex = getIn(action, 'data.relativeIndex');
      const sourceId = getIn(action, 'data.sourceId');
      const isSkipBack = relativeIndex < 0;
      const isModeAction = Boolean(modePandoraId);
      const service = isModeAction
        ? APSServices.actionMode
        : isSkipBack
        ? APSServices.actionPrevious
        : APSServices.actionSkip;

      if (isModeAction) {
        result = yield call(service, action.data);
      } else {
        const playableIndex = yield call(
          getNextPlayablePlaylistIndex,
          sourceId,
          item.pandoraId,
          isSkipBack,
        );
        if (isNumber(playableIndex)) {
          result = yield call(Helpers.playbackSource, {
            sourceId,
            index: playableIndex,
          });
        } else {
          result = yield call(service, action.data);
        }
      }
      yield call(playCurrentItem, {
        data: { sourceId: getIn(action, 'data.sourceId'), item: result.item },
      });
    } catch (error) {
      yield spawn(Helpers.onActionEventCurrentFailure, {
        error,
        apsEndpoint: D.Constants.Api.ApiEndpoints.ACTION_SKIP,
        originalAction: action,
      });
    }
  }
}
